/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { RefObject } from 'react';
import { ITableVisualizationDynamicProperties, IBodyContainerProps } from '../types/tableVisualizationTypes';

// Next constant is used to specify how much in percents would we take from the container's height
// to render extra rows in table to allow user to scroll table without pulling new data from the server.
// The more the value the less frequently client would load data from the server.
const EXTRA_SPACE_TO_TAKE = 0.65;

export function checkViewPortDisplay(viewPortRef: RefObject<any>, props: IBodyContainerProps): void {
    const { rows, dynamicProperties, onUpdateDynamicProperties } = props;

    const viewPortHeight = viewPortRef.current.offsetHeight;
    const viewPortScrollTop = viewPortRef.current.scrollTop;
    const currentOffsetTop = dynamicProperties.offsetTop;

    let offsetTop = Math.max(0, viewPortScrollTop - Math.abs(viewPortHeight * EXTRA_SPACE_TO_TAKE));
    let adjustedOffsetTop = 0;
    for (let i = 0; i < dynamicProperties.rowHeights.length; i += 1) {
        const newValue = adjustedOffsetTop + dynamicProperties.rowHeights[i];
        if (offsetTop <= newValue) {
            break;
        }
        adjustedOffsetTop = newValue;
    }
    offsetTop = adjustedOffsetTop;

    const dataHeightBasedOnRows = rows.reduce(((acc, r) => acc + r.height), 0);

    const onTop = viewPortScrollTop <= currentOffsetTop;
    const atBottom = viewPortScrollTop + viewPortHeight >= dataHeightBasedOnRows + currentOffsetTop;

    if (onTop || atBottom) {
        const newDynamicProperties = {
            offsetTop,
            viewPortHeight
        } as ITableVisualizationDynamicProperties;
        onUpdateDynamicProperties(newDynamicProperties);
    }
}

export function checkUpdateVisualData(viewPortRef: RefObject<any>, props: IBodyContainerProps): void {
    const tableBodyContainer: HTMLDivElement = viewPortRef.current.getElementsByTagName('table')[0];
    const domRows: HTMLCollectionOf<any> = tableBodyContainer.getElementsByClassName('jr-mDatatable-row');
    if (!domRows.length) {
        return;
    }

    const {
        rows, dynamicProperties, onUpdateDynamicProperties
    } = props;

    let hasNew = false;
    let minRowHeight = Number.MAX_VALUE;
    const newRowHeights = [];
    const rowsOffset = +domRows[0].dataset.absoluteIndex;

    for (let i = 0; i < domRows.length; i += 1) {
        const row = rows[i];
        if (!row.isLoading) {
            const existingRowHeight = dynamicProperties.rowHeights[i + rowsOffset];
            const domRow = domRows[i];
            const currentRowHeight = domRow.offsetHeight;
            // Next code gives more accurate result in terms of getting real cell height, but it doesn't work
            // in headless chrome and causes regressions like JS-63086. So, the decision is to get back to previous
            // way of using 'offsetHeight' (which works almost fine) and then later investigate why it didnt' work.
            // More details on this bug see in teh comments to JS-63086.
            // const rect = domRow.getBoundingClientRect();
            // const currentRowHeight = rect.height;
            if (existingRowHeight) {
                newRowHeights[i] = currentRowHeight;
            } else {
                newRowHeights[i] = currentRowHeight;
                minRowHeight = Math.min(minRowHeight, newRowHeights[i]);
                hasNew = true;
            }
        } else {
            newRowHeights[i] = 0;
        }
    }

    if (hasNew) {
        // update heights, same object
        for (let i = 0; i < newRowHeights.length; i += 1) {
            dynamicProperties.rowHeights[i + rowsOffset] = newRowHeights[i];
        }
        const newDynamicProperties = {
            minRowHeight: dynamicProperties.minRowHeight < 0 ? minRowHeight : Math.min(minRowHeight, dynamicProperties.minRowHeight),
            contentHeight: dynamicProperties.rowHeights.reduce((a, h) => a + h, 0),
            rowHeights: dynamicProperties.rowHeights
        } as ITableVisualizationDynamicProperties;
        onUpdateDynamicProperties(newDynamicProperties);
    }
}
